import { useRef, KeyboardEventHandler, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import AuthLayout from './AuthLayout';
import { Box, Stack, Typography } from '@mui/material';
import TextInput from 'components/Common/TextInput';
import CustomButton from 'components/Common/Button';

type ValidateCodeInputs = {
    code1: string;
    code2: string;
    code3: string;
    code4: string;
    code5: string;
    code6: string;
};

const VerifyPinCode = () => {
    const focus1 = useRef<HTMLInputElement>(null);
    const focus2 = useRef<HTMLInputElement>(null);
    const focus3 = useRef<HTMLInputElement>(null);
    const focus4 = useRef<HTMLInputElement>(null);
    const focus5 = useRef<HTMLInputElement>(null);
    const focus6 = useRef<HTMLInputElement>(null);

    const focusRefs = [focus1, focus2, focus3, focus4, focus5, focus6];
    const [error, setError] = useState('');
    const { control, handleSubmit, getValues } = useForm<ValidateCodeInputs>({
        defaultValues: {
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
        },
    });

    const validateCode = async (data: ValidateCodeInputs) => {
        const code = Object.values(data).join('');
        if (code.length < 6) {
            setError('Please fill all the fields.');
        } else {
            setError('');
        }
    };

    // const handlePaste = (e: React.ClipboardEvent) => {
    //     e.preventDefault();
    //     const pastedData = e.clipboardData.getData('Text').replace(/[^0-9]/g, '').substring(0, 6);
    //     pastedData.split('').forEach((char, i) => {
    //         setValue(`code${i + 1}` as keyof ValidateCodeInputs, char);
    //         const currentRef = focusRefs[i]?.current;
    //         if (currentRef) {
    //             currentRef.value = char;
    //             currentRef.dispatchEvent(new Event('input', { bubbles: true }));
    //             if (i < 5 && focusRefs[i + 1]?.current) {
    //                 focusRefs[i + 1].current?.focus();
    //             }

    //         }
    //     });
    // };

    const handleBackspace: KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (e.key === 'Backspace') {
            const currentRef = document.activeElement as HTMLInputElement;
            const currentIndex = focusRefs.findIndex((ref) => ref?.current === currentRef);
            if (currentIndex > 0 && currentRef.value === '') {
                const prevRef = focusRefs[currentIndex - 1]?.current;
                if (prevRef) {
                    prevRef.focus();
                    prevRef.value = '';
                }
            }
        }
    };

    const handleChange = (e: any, index: number) => {
        const text = e.target.value.replace(/[^0-9]/g, '').substring(0, 1);
        if (text && index < 5) {
            focusRefs[index + 1]?.current?.focus();
        }
        const values = getValues();
        const allFilled = Object.values(values).every((value) => value);
        if (allFilled) {
            setError('');
        }
    };

    return (
        <AuthLayout>
            <div className="Auth-form">
                <Box p={5}>
                    <Typography variant="h4">Account verification</Typography>
                    <br />
                    <Typography>
                        We have sent a verification code to the email used to create this account. Please provide that code in order to
                        validate this account.
                    </Typography>
                    <Stack direction="column" alignItems="center">
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                margin: '20px 0',
                            }}
                        >
                            <Controller
                                name="code1"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus1}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            handleChange(e, 0);
                                        }}
                                        // onPaste={handlePaste}
                                        onKeyDown={handleBackspace}
                                    />
                                )}
                            />
                            <Controller
                                name="code2"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus2}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            handleChange(e, 1);
                                        }}
                                        // onPaste={handlePaste}
                                        onKeyDown={handleBackspace}
                                    />
                                )}
                            />
                            <Controller
                                name="code3"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus3}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            handleChange(e, 2);
                                        }}
                                        // onPaste={handlePaste}
                                        onKeyDown={handleBackspace}
                                    />
                                )}
                            />
                            <Controller
                                name="code4"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus4}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            handleChange(e, 3);
                                        }}
                                        // onPaste={handlePaste}
                                        onKeyDown={handleBackspace}
                                    />
                                )}
                            />
                            <Controller
                                name="code5"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus5}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            handleChange(e, 4);
                                        }}
                                        // onPaste={handlePaste}
                                        onKeyDown={handleBackspace}
                                    />
                                )}
                            />
                            <Controller
                                name="code6"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextInput
                                        icon={null}
                                        inputProps={{ maxLength: 1 }}
                                        variant="standard"
                                        placeholder=""
                                        style={{
                                            width: '40px',
                                            fontSize: '24px',
                                            textAlign: 'center',
                                            margin: '0 5px',
                                            background: 'transparent',
                                        }}
                                        value={value}
                                        inputRef={focus6}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            handleChange(e, 5);
                                        }}
                                        // onPaste={handlePaste}
                                        onKeyDown={handleBackspace}
                                    />
                                )}
                            />
                        </div>
                        {error && (
                            <Box>
                                <Typography variant="body1" sx={{ color: 'red' }}>
                                    {error}
                                </Typography>
                            </Box>
                        )}
                        <Stack alignItems="center" direction="row">
                            <Typography>Didn’t receive a code?</Typography>
                            {/* {!loading && ( */}
                            <CustomButton
                                sx={{
                                    '&:hover': { background: 'none', boxShadow: 'none' },
                                    color: 'black',
                                    background: 'none',
                                    fontSize: 16,
                                    textDecoration: 'underline',
                                }}
                            >
                                Resend Code
                            </CustomButton>
                            {/* )} */}
                        </Stack>
                    </Stack>
                    <Box sx={{ textAlign: 'center', mt: 10 }}>
                        <CustomButton width={'100%'} onClick={handleSubmit(validateCode)}>
                            Continue
                        </CustomButton>
                    </Box>
                </Box>
            </div>
        </AuthLayout>
    );
};

export default VerifyPinCode;
